/**
 * Two Thousand — animated canvas mesh gradient.
 * Simplified Stripe-mesh style: two color blobs orbiting on a canvas,
 * with smooth interpolation to a target color when setColor() is called.
 *
 * Exposes window.__twoThousandGradient = { setColor(hex, weight), stop() }
 */
(() => {
  const canvas = document.getElementById('gradient-canvas');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  const styles = getComputedStyle(document.documentElement);
  const readVar = (name, fallback) => (styles.getPropertyValue(name).trim() || fallback);

  const hexToRgb = (hex) => {
    const s = hex.replace('#', '').trim();
    const full = s.length === 3 ? s.split('').map(c => c + c).join('') : s;
    const int = parseInt(full || '000000', 16);
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
  };
  const rgbStr = ({ r, g, b }, a = 1) => `rgba(${r|0},${g|0},${b|0},${a})`;
  const lerp = (a, b, t) => a + (b - a) * t;
  const lerpRgb = (a, b, t) => ({ r: lerp(a.r, b.r, t), g: lerp(a.g, b.g, t), b: lerp(a.b, b.b, t) });

  // HSL helpers — used to force the extracted color into a saturated,
  // vivid state before pushing it into the gradient. Without this the wash
  // can look pastel/muddy for images with mixed hues (white shirt + red
  // print returns a washed-pink instead of a punchy red).
  const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) { h = 0; s = 0; }
    else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = ((b - r) / d + 2);
      else h = ((r - g) / d + 4);
      h /= 6;
    }
    return { h, s, l };
  };
  const hslToRgb = (h, s, l) => {
    let r, g, b;
    if (s === 0) { r = g = b = l; }
    else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    return { r: r * 255, g: g * 255, b: b * 255 };
  };
  // Force saturation + clamp luminosity into a punchy range.
  const punchify = (rgb, targetL) => {
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    const s = Math.max(hsl.s, 0.85); // floor saturation
    const l = targetL !== undefined ? targetL : Math.min(0.55, Math.max(0.35, hsl.l));
    return hslToRgb(hsl.h, s, l);
  };

  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  const resize = () => {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(window.innerWidth * dpr);
    canvas.height = Math.round(window.innerHeight * dpr);
    canvas.style.width = window.innerWidth + 'px';
    canvas.style.height = window.innerHeight + 'px';
  };
  window.addEventListener('resize', resize, { passive: true });
  resize();

  const speedSetting = parseFloat(readVar('--gradient-speed', '3')) || 3;
  // Base cadence: at speed=3 → full orbit ~60 seconds. Slower & cinematic
  // (SKYLRK-style — motion is barely perceptible, feels ambient not busy).
  const speed = Math.max(0, speedSetting) * 0.0001;

  let currentA = hexToRgb(readVar('--gradient-c1', '#9DD5F1'));
  let currentB = hexToRgb(readVar('--gradient-c2', '#8FD5FF'));
  let targetA = { ...currentA };
  let targetB = { ...currentB };

  const setColor = (hex, weight = 0.8, immediate = false) => {
    if (!hex) return;
    const raw = hexToRgb(hex);
    const rawLum = (raw.r + raw.g + raw.b) / 3;

    // BLACK-product branch — extractor sends a very-dark hex when the product
    // is predominantly black (no vibrant hue to punchify). Set a true dark
    // palette directly; skipping punchify avoids the "gray input → colored
    // output" side effect (punchify forces saturation ≥ 0.85 on any hue).
    if (rawLum < 30) {
      // Matte-black palette: never true 0,0,0 — the grain overlay needs some
      // luminance to bite into. SKYLRK's dark look works because their black
      // is a soft charcoal, not RGB(0,0,0), so the noise reads as texture.
      const darkA = { r: 48, g: 48, b: 52 };  // charcoal highlight orb
      const darkB = { r: 22, g: 22, b: 26 };  // matte dark base
      const c1 = hexToRgb(readVar('--gradient-c1', '#9DD5F1'));
      const c2 = hexToRgb(readVar('--gradient-c2', '#8FD5FF'));
      targetA = weight >= 1 ? darkA : lerpRgb(c1, darkA, weight);
      targetB = weight >= 1 ? darkB : lerpRgb(c2, darkB, weight);
      if (immediate) { currentA = { ...targetA }; currentB = { ...targetB }; }
      return;
    }

    // Two variants of the SAME hue, both fully saturated. A is the medium
    // luminosity for the moving highlight; B is a deeper version for the
    // base fill. Both stay punchy so the wash never dips into pastel
    // territory when the highlight blob orbits away.
    const hueA = punchify(raw, 0.48);
    const hueB = punchify(raw, 0.28);
    // Weight lets colors NOT-fully-applied still work (e.g. hover previews
    // at partial weight). PDP passes 1.0 → colors go fully to hueA/hueB.
    targetA = weight >= 1 ? hueA : lerpRgb(hexToRgb(readVar('--gradient-c1', '#9DD5F1')), hueA, weight);
    targetB = weight >= 1 ? hueB : lerpRgb(hexToRgb(readVar('--gradient-c2', '#8FD5FF')), hueB, weight);
    // immediate=true snaps to target (no fade). Used on PDP first paint so
    // the product's predominant color shows up instantly, not after a fade.
    if (immediate) { currentA = { ...targetA }; currentB = { ...targetB }; }
  };

  let t0 = performance.now();
  let raf;
  const draw = (t) => {
    const elapsed = (t - t0) * speed;

    // Smoothly interpolate current colors toward target. Lower factor =
    // slower transition. 0.015 ≈ ~2.5s for a color swap — matches SKYLRK's
    // gradual, cinematic wash.
    currentA = lerpRgb(currentA, targetA, 0.015);
    currentB = lerpRgb(currentB, targetB, 0.015);

    const w = canvas.width, h = canvas.height;
    const maxDim = Math.max(w, h);

    // BASE FILL — the deep tone covers the full canvas.
    ctx.fillStyle = rgbStr(currentB, 1);
    ctx.fillRect(0, 0, w, h);

    // TWO ORBITING ORBS — original vibrant treatment. The canvas has a
    // CSS filter: blur(120px), so the orbs' edges dissolve completely
    // and no visible circle or line remains — just soft breathing color.
    const cxA = w * (0.5 + Math.sin(elapsed) * 0.5);
    const cyA = h * (0.5 + Math.cos(elapsed * 0.7) * 0.45);
    const rA = maxDim * 0.7;
    const gA = ctx.createRadialGradient(cxA, cyA, 0, cxA, cyA, rA);
    gA.addColorStop(0,    rgbStr(currentA, 1));
    gA.addColorStop(0.35, rgbStr(currentA, 0.75));
    gA.addColorStop(0.75, rgbStr(currentA, 0.2));
    gA.addColorStop(1,    rgbStr(currentA, 0));
    ctx.fillStyle = gA;
    ctx.fillRect(0, 0, w, h);

    const cxB = w * (0.5 + Math.cos(elapsed * 1.3 + 1.5) * 0.4);
    const cyB = h * (0.5 + Math.sin(elapsed * 1.1 + 0.8) * 0.4);
    const rB = maxDim * 0.42;
    const gB = ctx.createRadialGradient(cxB, cyB, 0, cxB, cyB, rB);
    gB.addColorStop(0,   rgbStr(currentA, 0.8));
    gB.addColorStop(0.5, rgbStr(currentA, 0.35));
    gB.addColorStop(1,   rgbStr(currentA, 0));
    ctx.fillStyle = gB;
    ctx.fillRect(0, 0, w, h);

    raf = requestAnimationFrame(draw);
  };

  const start = () => { if (!raf) raf = requestAnimationFrame(draw); };
  const stop = () => { if (raf) { cancelAnimationFrame(raf); raf = null; } };

  // Respect prefers-reduced-motion
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq.matches) {
    // Still draw one static frame
    draw(performance.now());
  } else {
    start();
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop(); else start();
  });

  // Reset — go BACK to the soft, unsaturated CSS-var colors (skips
  // punchify so the resting state is the tenue palette the merchant
  // configured, not a punchified version of it).
  const resetColor = () => {
    targetA = hexToRgb(readVar('--gradient-c1', '#9DD5F1'));
    targetB = hexToRgb(readVar('--gradient-c2', '#8FD5FF'));
  };

  window.__twoThousandGradient = { setColor, resetColor, stop, start };
})();
